import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Send, User, Mail, Phone, Briefcase, GraduationCap, Globe, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  useVisaRecommenderPreference,
  recommendationToFormFields,
} from '@/hooks/useVisaRecommenderPreference';
import PrefillBanner from './PrefillBanner';
import PrivacyConsentNotice from './PrivacyConsentNotice';
import { trackFormSubmit } from '@/services/analytics';
import { useContactChannel } from '@/hooks/useContactChannel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Service catalogues are centralised in /app/frontend/src/config/contactChannels.js
// and surfaced via the `serviceTypes` field of `useContactChannel()`. We import
// nothing here — see `serviceTypes = channel.serviceTypes` inside the component.

const countries = [
  'Canada',
  'Australia',
  'USA',
  'UK',
  'Germany',
  'UAE',
  'New Zealand',
  'Other European Countries',
  'Other',
];

const qualifications = [
  'High School',
  'Diploma',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'PhD/Doctorate',
  'Professional Certification',
];

const experienceOptions = [
  'Fresher (0-1 years)',
  '1-3 years',
  '3-5 years',
  '5-10 years',
  '10+ years',
];

const ConsultationForm = ({ variant = 'default', className = '', source = '' }) => {
  const channel = useContactChannel();
  // Service catalogue centralised in config/contactChannels.js, exposed
  // per-vertical via the hook. SA pages → SA services, recruitment pages
  // → recruitment services. Cross-vertical cross-pollination impossible.
  const serviceTypes = channel.serviceTypes;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    country: '',
    qualification: '',
    experience: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pull the latest "Best Country for Me" quiz answers from localStorage (if any).
  // We auto-fill country + service_type on first render, then let the user override.
  // A small "Pre-filled from your visa recommendation" banner surfaces so the
  // behaviour is transparent (and the user can clear it with one click).
  const { pref: visaPref, clear: clearVisaPref } = useVisaRecommenderPreference();
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    const next = recommendationToFormFields(visaPref);
    if (!next) {
      // Sync: if pref cleared by another form instance, hide banner here too.
      setPrefilled(false);
      return;
    }
    setFormData(prev => {
      const updates = {};
      Object.keys(next).forEach((k) => {
        if (prev[k]) return;
        // Only auto-fill `service_type` if the recommended value is part
        // of the catalogue for the current vertical. Prevents stale cross-
        // vertical service types (e.g. SA recommendation showing up on a
        // recruitment page) from polluting CRM data.
        if (k === 'service_type' && !serviceTypes.includes(next[k])) return;
        updates[k] = next[k];
      });
      if (Object.keys(updates).length === 0) return prev;
      setPrefilled(true);
      return { ...prev, ...updates };
    });
  }, [visaPref, serviceTypes]);

  const dismissPrefill = () => {
    setPrefilled(false);
    clearVisaPref();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Auto-inject vertical tagging so the CRM and Mongo lead-store can
      // segment between Overseas Placement vs Study Abroad enquiries.
      const verticalSource = source || `consultation-form-${channel.vertical}`;
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        source: verticalSource,
        message: `[${channel.leadServiceTag}] ${formData.message || ''}`.trim(),
      });

      // Conversion: a generic form lead. Pages can pass a more specific
      // `source` (e.g. study-abroad-why-zafco-inline-form) so Google Ads
      // can segment by campaign placement. The analytics layer also
      // auto-appends the vertical, so GA4 + Ads see segregated funnels.
      trackFormSubmit(verticalSource, {
        service_type: formData.service_type,
        country: formData.country,
        vertical: channel.vertical,
      });

      toast.success('Thank you! Our team will contact you shortly.');

      // Open WhatsApp on the vertical's WhatsApp number
      const message = `Hello Zafco, I'm ${formData.name}. I'm interested in ${formData.service_type} for ${formData.country}. Please contact me at ${formData.phone}.`;
      window.open(channel.whatsappUrlWith(message), '_blank', 'noopener,noreferrer');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        country: '',
        qualification: '',
        experience: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCompact = variant === 'compact';
  const isDark = variant === 'dark';
  
  // Styling based on variant
  const inputClasses = isDark 
    ? "pl-10 h-12 bg-slate-800/50 border-white/10 text-white placeholder:text-slate-500 focus:bg-slate-800 focus:border-[#C5A028]/50"
    : "pl-10 h-12 bg-gray-50 border-gray-200 focus:bg-white";
  
  const iconClasses = isDark ? "text-slate-500" : "text-gray-400";
  
  const buttonClasses = isDark
    ? "w-full h-12 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold rounded-full"
    : "w-full h-12 bg-zafco-blue hover:bg-zafco-blue-dark text-white font-semibold";

  return (
    <motion.form 
      onSubmit={handleSubmit}
      className={`${isCompact || isDark ? 'space-y-4' : 'space-y-5'} ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="consultation-form"
    >
      {/* Auto-fill banner — surfaces when ConsultationForm inherits the latest
          "Best Country for Me" recommender answers from localStorage. */}
      {prefilled && (
        <PrefillBanner
          variant={isDark ? 'dark' : 'light'}
          onDismiss={dismissPrefill}
          message={<>We&apos;ve set <em>Destination</em> and <em>Service</em> based on your quiz answers — feel free to change.</>}
        />
      )}

      {/* Name & Email Row */}
      <div className={`grid ${isCompact || isDark ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4`}>
        <div className="relative">
          <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconClasses}`} />
          <Input
            type="text"
            placeholder="Full Name *"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={inputClasses}
            data-testid="form-name"
            required
          />
        </div>
        <div className="relative">
          <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconClasses}`} />
          <Input
            type="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className={inputClasses}
            data-testid="form-email"
            required
          />
        </div>
      </div>

      {/* Phone */}
      <div className="relative">
        <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconClasses}`} />
        <Input
          type="tel"
          placeholder="Phone Number (with country code) *"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          className={inputClasses}
          data-testid="form-phone"
          required
        />
      </div>

      {/* Service Type & Country */}
      <div className={`grid ${isCompact || isDark ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4`}>
        <div className="relative">
          <Briefcase className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconClasses} z-10`} />
          <Select value={formData.service_type} onValueChange={(val) => handleChange('service_type', val)}>
            <SelectTrigger className={isDark ? "pl-10 h-12 bg-slate-800/50 border-white/10 text-white data-[placeholder]:text-slate-400" : "pl-10 h-12 bg-gray-50 border-gray-200 data-[placeholder]:text-gray-600"} data-testid="form-service">
              <SelectValue placeholder="Select Service Type *" />
            </SelectTrigger>
            <SelectContent className={isDark ? "bg-slate-800 border-white/10" : ""}>
              {serviceTypes.map((service) => (
                <SelectItem key={service} value={service} className={isDark ? "text-white hover:bg-slate-700" : ""}>{service}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Globe className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconClasses} z-10`} />
          <Select value={formData.country} onValueChange={(val) => handleChange('country', val)}>
            <SelectTrigger className={isDark ? "pl-10 h-12 bg-slate-800/50 border-white/10 text-white data-[placeholder]:text-slate-400" : "pl-10 h-12 bg-gray-50 border-gray-200 data-[placeholder]:text-gray-600"} data-testid="form-country">
              <SelectValue placeholder="Select Country *" />
            </SelectTrigger>
            <SelectContent className={isDark ? "bg-slate-800 border-white/10" : ""}>
              {countries.map((country) => (
                <SelectItem key={country} value={country} className={isDark ? "text-white hover:bg-slate-700" : ""}>{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Qualification & Experience */}
      {!isCompact && !isDark && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <GraduationCap className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconClasses} z-10`} />
            <Select value={formData.qualification} onValueChange={(val) => handleChange('qualification', val)}>
              <SelectTrigger className="pl-10 h-12 bg-gray-50 border-gray-200 data-[placeholder]:text-gray-600" data-testid="form-qualification">
                <SelectValue placeholder="Highest Qualification" />
              </SelectTrigger>
              <SelectContent>
                {qualifications.map((qual) => (
                  <SelectItem key={qual} value={qual}>{qual}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <Briefcase className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${iconClasses} z-10`} />
            <Select value={formData.experience} onValueChange={(val) => handleChange('experience', val)}>
              <SelectTrigger className="pl-10 h-12 bg-gray-50 border-gray-200 data-[placeholder]:text-gray-600" data-testid="form-experience">
                <SelectValue placeholder="Years of Experience" />
              </SelectTrigger>
              <SelectContent>
                {experienceOptions.map((exp) => (
                  <SelectItem key={exp} value={exp}>{exp}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Message */}
      {!isCompact && !isDark && (
        <div className="relative">
          <MessageSquare className={`absolute left-3 top-3 w-5 h-5 ${iconClasses}`} />
          <Textarea
            placeholder="Tell us about your requirements (optional)"
            value={formData.message}
            onChange={(e) => handleChange('message', e.target.value)}
            className="pl-10 min-h-[100px] bg-gray-50 border-gray-200 focus:bg-white"
            data-testid="form-message"
          />
        </div>
      )}

      {/* Submit Button */}
      <PrivacyConsentNotice tone={isDark ? 'dark' : 'light'} testid="consultation-privacy-consent" />
      <Button 
        type="submit" 
        disabled={isSubmitting}
        className={isDark 
          ? "w-full h-12 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold rounded-full transition-all" 
          : "w-full h-14 bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold text-lg hover:shadow-lg hover:shadow-zafco-gold/30 transition-all"
        }
        data-testid="form-submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Get Free Assessment
          </>
        )}
      </Button>

      <p className={`text-center text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
        We'll respond within 60 seconds via WhatsApp!
      </p>
    </motion.form>
  );
};

export default ConsultationForm;
